import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  Chart,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';

import styles from './ReportsChart.module.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

type Props = {
  data: ChartData<'bar'>;
};

export const defaultOptions = {
  plugins: {
    legend: {
      position: 'top' as const,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
};

export function ReportsChart({ data }: Props) {
  const chartRef = useRef<ChartJSOrUndefined<'bar'>>(null);
  const [chartData, setChartData] = useState<ChartData<'bar'> | null>(null);

  useEffect(() => {
    let chart: Chart | null = null;
    if (chartRef.current) {
      chart = chartRef.current.getContext().chart;
    }

    if (!chart) {
      setChartData(data);
      return;
    }

    const labelsLengthIsEqul = data.labels && chart.data.labels && data.labels.length === chart.data.labels.length;
    const datasetsLengthIsEqul = data.datasets && chart.data.datasets && data.datasets.length === chart.data.datasets.length;

    if (!labelsLengthIsEqul || !datasetsLengthIsEqul) {
      setChartData(data);
      return;
    }

    for (let datasetIndex = 0; datasetIndex < data.datasets.length; datasetIndex += 1) {
      const dataset = data.datasets[datasetIndex];
      const chartDataset = chart.data.datasets[datasetIndex];

      if (!dataset || !chartDataset || dataset.data.length !== chartDataset.data.length) {
        setChartData(data);
        return;
      }

      if (dataset.label !== chartDataset.label) {
        chartDataset.label = dataset.label;
      }

      if (dataset.backgroundColor !== chartDataset.backgroundColor) {
        chartDataset.backgroundColor = dataset.backgroundColor;
      }

      for (let dataIndex = 0; dataIndex < dataset.data.length; dataIndex += 1) {
        if (dataset.data[dataIndex] !== chartDataset.data[dataIndex]) {
          chartDataset.data[dataIndex] = dataset.data[dataIndex];
        }
      }
    }

    chart.update();
  }, [data]);

  return (
    <div className={styles.chartContainer}>
      {chartData && (
        <Bar ref={chartRef} options={defaultOptions} data={chartData} />
      )}
    </div>
  );
}
